/**
 * Normalises raw AI output for Markdown rendering.
 *
 * The models are asked for clean Markdown, but occasionally wrap the whole
 * response in a ```json / ``` code fence or return a bare JSON object. Rendering
 * that verbatim shows raw JSON to the user. This helper:
 *   1. strips a single code fence that wraps the entire string, and
 *   2. if the result is a bare JSON object/array, pretty-prints it inside a
 *      fenced block so ReactMarkdown shows it cleanly instead of as a wall of text.
 * Plain Markdown passes through untouched.
 */
export function normalizeAiContent(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();

  // 1) Strip a single wrapping code fence (```json … ``` / ```markdown … ``` / ``` … ```).
  const fenceMatch = s.match(/^```(?:json|markdown|md)?\s*\n?([\s\S]*?)\n?```$/i);
  if (fenceMatch) {
    s = fenceMatch[1].trim();
  }

  // 2) If the whole thing is a JSON object/array, pretty-print it in a fenced block.
  if (/^[[{]/.test(s) && /[\]}]$/.test(s)) {
    try {
      const parsed = JSON.parse(s);
      return '```json\n' + JSON.stringify(parsed, null, 2) + '\n```';
    } catch {
      // Not valid JSON — fall through and render as-is.
    }
  }

  return s;
}

export default normalizeAiContent;
